const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.utils.sectionRenderer.fetchSingle(
      section,
      url,
    )

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach(el => newItems.appendChild(el))

    const products = document.querySelectorAll(
      '.prd-List_Item .prd-Card_FauxLink',
    )

    if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)

      if (
        e.target.hasAttribute('data-collection-filters-el') &&
        e.target.getAttribute('data-collection-filters-el') ===
          'load-more-trigger'
      ) {
        products[products.length - 1].focus()
      }
    } else {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach(image => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      .setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      .setAttribute('aria-hidden', (newElements.length > 0).toString())
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const loadMoreSelector = `${this.element.els['load-more'].selector}`
    const paginationElement = e.target.closest(loadMoreSelector)
    this.element.log(
      'getPaginationDirection: closest result (paginationElement):',
      paginationElement,
    )

    if (!paginationElement) {
      this.element.log(
        'getPaginationDirection: closest did not find loadMoreElement, returning null.',
      )
      return null
    }

    this.element.log('getPaginationDirection: returning NEXT.')
    return 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    const loadMoreContainer = this.element.els['load-more']?.element
    if (!loadMoreContainer) return

    // Find the link within the new HTML
    const newLink = window.BAO.utils.getElement(
      `${this.element.els['load-more'].selector} a`,
      {
        context: html,
      },
    )

    // Find the link within the current container
    const currentLink = window.BAO.utils.getElement('a', {
      context: loadMoreContainer,
    })

    if (newLink) {
      if (currentLink) {
        currentLink.href = newLink.href
        loadMoreContainer.style.display = ''
      } else {
        loadMoreContainer.innerHTML = window.BAO.utils.getElement(
          this.element.els['load-more'].selector,
          { context: html },
        ).innerHTML
      }
    } else {
      loadMoreContainer.style.display = 'none'
      if (currentLink) {
        currentLink.href = '#'
      }
    }
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)
    this.renderProductCount(newHTMLDom)
    this.renderAdditional(newHTMLDom)
    this.renderActiveFilterCount(newHTMLDom)
    this.renderPaginationLabel(newHTMLDom)

    this.element.loading = false
  }

  /**
   * Renders the active filter count display.
   *
   * @param {Document} document - The parsed HTML document from the server response.
   * @returns {void}
   */
  renderActiveFilterCount (document) {
    const activeFilterCountSelector = this.element.els['active-facets-count']
      .selector

    const newActiveFilterCountEl = window.BAO.utils.getElement(
      activeFilterCountSelector,
      { context: document },
    )
    const currentActiveFilterCountEls = window.BAO.utils.getElements(
      activeFilterCountSelector,
    )

    if (!newActiveFilterCountEl) {
      this.element.warn(
        `Could not find new active filter count element with selector: ${activeFilterCountSelector}`,
      )
      return
    }

    if (
      !currentActiveFilterCountEls ||
      currentActiveFilterCountEls.length === 0
    ) {
      this.element.warn(
        `Could not find current active filter count elements with selector: ${activeFilterCountSelector}`,
      )
      return
    }

    currentActiveFilterCountEls.forEach(el => {
      el.innerHTML = newActiveFilterCountEl.innerHTML
      // Optionally update attributes if needed, e.g., visibility based on count
      if (newActiveFilterCountEl.hasAttribute('hidden')) {
        el.setAttribute('hidden', '')
      } else {
        el.removeAttribute('hidden')
      }
    })

    this.element.log('Rendered active filter count.')
  }

  /**
   * Renders the pagination label display.
   *
   * @param {Document} document - The parsed HTML document from the server response.
   * @returns {void}
   */
  renderPaginationLabel (document) {
    const paginationLabelSelector =
      '[data-collection-pagination-label-external-el="label"]'

    const newPaginationLabelEl = window.BAO.utils.getElement(
      paginationLabelSelector,
      { context: document },
    )
    const currentPaginationLabelEls = window.BAO.utils.getElements(
      paginationLabelSelector,
    )

    if (!newPaginationLabelEl) {
      this.element.warn(
        `Could not find new pagination label element with selector: ${paginationLabelSelector}`,
      )
      return
    }

    if (!currentPaginationLabelEls || currentPaginationLabelEls.length === 0) {
      this.element.warn(
        `Could not find current pagination label elements with selector: ${paginationLabelSelector}`,
      )
      return
    }

    // Get the total count part (e.g., " of YYY") from the fetched label
    const newLabelText = newPaginationLabelEl.textContent || ''
    const totalCountMatch = newLabelText.match(/\s+of\s+\d+/)
    const totalCountString = totalCountMatch ? totalCountMatch[0] : '' // e.g., " of 123"

    // Count currently displayed items
    const currentItemCount =
      this.element.els['item-container']?.element?.querySelectorAll(
        '[data-collection-filters-el="item"]:not([data-promo])',
      )?.length || 0

    if (currentItemCount > 0) {
      const newLabelContent = `Showing 1 - ${currentItemCount}${totalCountString} products`
      currentPaginationLabelEls.forEach(el => {
        el.textContent = newLabelContent
      })
    } else {
      currentPaginationLabelEls.forEach(el => {
        el.innerHTML = newPaginationLabelEl.innerHTML // Revert to original behavior if 0 items
      })
    }

    this.element.log('Rendered pagination label.')
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    const getFacetSelector = facet => {
      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${this.element.els.facet.selector}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    const currentFacetElements = this.element.els.facet.elements
    const facetThatSubmitted = event?.submitter?.closest(
      this.element.els.facet.selector,
    )

    const facetsToRenderSimply = event
      ? currentFacetElements.filter(facet => facet !== facetThatSubmitted)
      : currentFacetElements

    this.element.log('renderFacets', {
      currentFacetElements,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach(facet => {
      const selector = getFacetSelector(facet)

      const accordionElement = facet.querySelector('details')
      const wasOpen = accordionElement ? accordionElement.open : false

      const el = window.BAO.utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML

      if (wasOpen) {
        const newAccordionElement = facet.querySelector('details')

        if (newAccordionElement) {
          newAccordionElement.open = true
        }
      }
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.els['active-facets'].elements
    if (activeFacets.length <= 0) return

    activeFacets.forEach(activeFacet => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: `${this.element.els['active-facets'].selector}`,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.utils.getElement(
        `[${this.element._elIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount (document) {
    const existingProductCounts = window.BAO.utils.getElements(
      this.element.els['product-count']?.selector,
    )

    const newProductCount = window.BAO.utils.getElement(
      this.element.els['product-count']?.selector,
      { context: document },
    )
    if (!newProductCount) return

    existingProductCounts.forEach(element => {
      const newText = newProductCount.textContent || newProductCount.innerText

      // Pluralise text if it contains "Item" and a number
      if (newText.includes('Item')) {
        const numberMatch = newText.match(/(\d+)/)

        if (numberMatch) {
          const count = parseInt(numberMatch[1], 10)
          const pluralizedText =
            count === 1 ? `${count} Item` : `${count} Items`

          element.textContent = pluralizedText
        } else {
          element.innerHTML = newProductCount.innerHTML
        }
      } else {
        element.innerHTML = newProductCount.innerHTML
      }
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = window.BAO.utils.getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = window.BAO.utils.getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    // eslint-disable-next-line quotes
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary', 'product-grid']
  }

  static get observedAttributes () {
    // Observe changes to the `aria-busy` attribute
    return ['aria-busy']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.DRAWER.OPENED,
      event => {
        if (
          event.detail.activeDrawerKey !==
          ('mobile-collection-filters' || 'mobile-results-filters')
        ) { return }

        const drawer = document.querySelector(
          'site-drawer[key="mobile-collection-filters"]',
        )
        window._previousFocus = document.activeElement

        setTimeout(() => {
          // eslint-disable-next-line no-undef
          trapFocus(drawer)
        }, 500)
      },
    )

    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: 'template-collection-product-grid',
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    this.isSubmitting = false
    this.scrollObserver = null
  }

  connectedCallback () {
    super.connectedCallback()

    this.setupListeners()
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.scrollObserver.disconnect()
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (name === 'aria-busy' && newValue === 'false') {
      this.setupListeners()
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = window.BAO.utils.debounce(
      e => this.onSubmit(e),
      500,
    )
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(this, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))

    this.addEventListener('keypress', e => {
      if (
        e.key === 'Enter' &&
        e.target.matches('.clc-ProductGridFacet_Label')
      ) {
        const checkboxId = e.target.getAttribute('for')
        const checkbox = document.getElementById(checkboxId)

        if (checkbox) {
          checkbox.checked = !checkbox.checked
          this.debouncedOnSubmit(e)
        }
      }
    })

    if (
      this.querySelector('[data-collection-filters-el="load-more-trigger"]')
    ) {
      this.listeners.add(
        this.querySelector('[data-collection-filters-el="load-more-trigger"]'),
        'click',
        this.onPaginationClick.bind(this),
      )
    }

    this.els['sort-option'].elements.forEach(sortOption => {
      this.listeners.add(sortOption, 'click', this.onSortByClick.bind(this))
    })

    this.handleFilterOnScroll()
  }

  handleFilterOnScroll () {
    const stickyEl = this.els.filterBar.element
    if (!stickyEl) throw new Error('Sticky element not found')

    const classToggle = `${stickyEl.classList[0]}-sticky`

    const topStr = getComputedStyle(stickyEl).top
    const stickyTop = Number.isFinite(parseFloat(topStr))
      ? parseFloat(topStr)
      : 0

    const sentinel = document.createElement('div')
    sentinel.setAttribute('aria-hidden', 'true')
    sentinel.style.cssText = 'position: relative; height: 1px; width: 1px;'
    stickyEl.before(sentinel)

    let isSticky = false

    const elHeight = stickyEl.offsetHeight
    stickyEl.style.setProperty('--sticky-height', `${elHeight}px`)

    window.addEventListener('resize', () => {
      stickyEl.style.setProperty(
        '--sticky-height',
        `${stickyEl.offsetHeight}px`,
      )
    })

    this.scrollObserver = new IntersectionObserver(
      ([entry]) => {
        const nowSticky = !entry.isIntersecting

        if (nowSticky === isSticky) return
        isSticky = nowSticky

        stickyEl.classList.toggle(classToggle, isSticky)
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${stickyTop - elHeight}px 0px 0px 0px`,
      },
    )

    this.scrollObserver.observe(sentinel)
  }

  onSortByClick (event) {
    const sortValue = event.currentTarget.dataset.value

    const select = this.querySelector('.clc-ProductGridFacets_Select')

    if (!select) return

    select.value = sortValue

    const parentForm = this.querySelector('#CollectionFiltersMobileForm')

    event.target.closest('#sortMenu').setAttribute('aria-expanded', 'false')

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const searchParams = new URLSearchParams(event.currentTarget.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    event.preventDefault()

    const parentForm = event.target.closest('form')

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  async onFormSubmit (event) {
    event.preventDefault()

    if (this.isSubmitting) return

    this.isSubmitting = true
    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    try {
      await this.renderPage({ searchParams, event })

      if (window.BAO.MediaBreakpoints.isSm) return

      this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
    } finally {
      this.isSubmitting = false
    }
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams

    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    // Prevent multiple clicks while loading
    if (this.loading) {
      this.log('onPaginationClick: Aborted, already loading.')
      return
    }

    const anchor = event.currentTarget
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    const searchParamsObject = new URLSearchParams(searchParams)
    const hasSizeFilter = Array.from(searchParamsObject.keys()).some(
      key => key.startsWith('filter.v.option.') && key.includes('size'),
    )

    if (hasSizeFilter && !searchParamsObject.has('filter.v.availability')) {
      searchParamsObject.set('filter.v.availability', '1')
    } else if (
      !hasSizeFilter &&
      searchParamsObject.has('filter.v.availability')
    ) {
      searchParamsObject.delete('filter.v.availability')
    }

    const searchParamsString = searchParamsObject.toString()

    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParamsString)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.section)

      await this.renderer.render(html, event)
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}
